/* eslint-disable no-unused-vars */
import Vue from "vue";
import {
  getDatabase,
  ref,
  get,
  set,
  update,
  remove,
  push,
  query,
  child,
  orderByChild,
  limitToLast,
  limitToFirst,
  startAt,
  endAt,
  equalTo,
  onValue,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  runTransaction,
  serverTimestamp,
} from "firebase/database";

import {
  // Channels
  FETCH_CHANNELS,
  CREATE_CHANNEL,
  GET_CHANNEL,
  GOTO_CHANNEL,
  // Topics
  FETCH_TOPICS,
  GET_TOPIC,
  GET_TOPIC_BY_ID,
  CREATE_TOPIC,
  UPDATE_TOPIC,
  CREATE_COMMENT,
  UPDATE_COMMENT,
  CREATE_REACTION,
  REMOVE_REACTION,
  GET_REACTION_BY_MSGID,
  CREATE_SUBCOMMENT,
  UPDATE_SUBCOMMENT,
  GET_COMMENT,
  GET_COMMENT_BY_ID,
  GET_SUBCOMMENT_BY_ID,
  UPDATE_USER_READING,
  UPDATE_USER_REPLAY,
  UPDATE_OPEN_COMMENT_EDIT,
  UPDATE_QUERY_RESULT,
  REMOVE_TOPIC,
  REMOVE_COMMENT,
  REMOVE_SUBCOMMENT,
  // Members
  GET_MEMBER,
  GET_MEMBER_BY_ID,
  FETCH_MEMBERS_ONLINE,
  FETCH_MEMBERS_TOTAL,
  // Modals
  OPEN_MODAL_ADD,
  CLOSE_MODAL_ADD,
  SHOW_DELETE_TOAST,
  FETCH_MEMBERS_BY_CHANNEL_ID,
  // Room
  CREATE_ROOM,
  FETCH_ROOMS,
  FETCH_MESSAGES_IN_ROOM,
  SEND_MESSAGE_IN_ROOM,
  UPDATE_MESSAGE_IN_ROOM,
  DELETE_MESSAGE_IN_ROOM,
  UPDATE_UNREAD_COUNT_IN_ROOM,
  LOGIN_ROOM,
} from "../actions";
import {
  // Channels
  SET_CHANNEL,
  // Topics
  SET_TOPIC,
  SET_COMMENT,
  SET_SUBCOMMENT,
  DEL_TOPIC,
  DEL_COMMENT,
  DEL_SUBCOMMENT,
  SET_REACTION,
  DEL_REACTION,
  SHOW_COMMENT_EDIT,
  HIDE_COMMENT_EDIT,
  SET_QUERY_RESULT,
  // Members
  SET_MEMBER,
  SET_MEMBERS_ONLINE,
  SET_TOTAL_MEMBERS,
  // Modal
  SHOW_MODAL_ADD,
  SET_DELETE_RESULT,
  // Common
  SET_KEY,
  //Room
  SET_ROOM,
  SET_TEXTUAL_MESSAGE,
  DEL_TEXTUAL_MESSAGE,
} from "../mutations";
import { TOPICS_IN_CHANNEL, COMMENTS_IN_TOPIC, SUBCOMMENTS_IN_COMMENTS, MESSAGES_IN_PAGE } from "@/constants/community";

import { triggerRoomLogin } from "@/common/functions";

const database = getDatabase();

const state = {
  channels: {},
  topics: {},
  comments: {},
  reactions: {},
  subcomments: {},
  rooms: {},
  textuals: {},

  members: {},
  membersOnline: {},
  membersTotal: {},

  currentChannelId: null,
  currentAction: null,

  isShowingModalAdd: false,
  isOpenCommentEdit: null,
  queryResult: null,
  deleteResult: null,
  errors: null,
};

const getters = {
  channels({ channels }) {
    return channels;
  },
  rooms({ rooms }) {
    return rooms;
  },
  textuals({ textuals }) {
    return textuals;
  },
  topics({ topics }) {
    return topics;
  },
  comments({ comments }) {
    return comments;
  },
  subcomments({ subcomments }) {
    return subcomments;
  },
  reactions({ reactions }) {
    return reactions;
  },
  members({ members }) {
    return members;
  },
  membersOnline({ membersOnline }) {
    return membersOnline;
  },
  membersTotal({ membersTotal }) {
    return membersTotal;
  },

  isShowingModalAdd({ isShowingModalAdd }) {
    return isShowingModalAdd;
  },

  isOpenCommentEdit({ isOpenCommentEdit }) {
    return isOpenCommentEdit;
  },

  queryResult({ queryResult }) {
    return queryResult;
  },
  deleteResult({ deleteResult }) {
    return deleteResult;
  },
  hasErrors(state) {
    return state.errors;
  },
};

const getAllMembersInChannel = async (channelId) => {
  const result = ref(database, `members`);
  let notiMembers = [];
  const snpMembers = await get(result);

  if (snpMembers.exists()) {
    Object.entries(snpMembers.val()).forEach((member) => {
      const memberData = { id: member[0], ...member[1] };
      const channels = memberData.channels;
      if (channels) {
        Object.entries(channels).forEach((channel) => {
          if (channel[0] === channelId) {
            notiMembers.push(member[0]);
          }
        });
      }
    });
  }
  return notiMembers;
};

const addNotificationToUser = async (memberId, notificationId, eventData) => {
  await set(ref(database, `members/${memberId}/notifications/events/${notificationId}`), { isRead: false, ...eventData });
  await runTransaction(ref(database, `members/${memberId}/notifications/unreadCount`), (unreadCount) => {
    if (unreadCount) {
      unreadCount++;
    } else {
      unreadCount = 1;
    }
    return unreadCount;
  });
};

const mutations = {
  [SET_CHANNEL](state, { id, ...others }) {
    Vue.set(state.channels, id, { ...others });
  },
  [SET_ROOM](state, { id, ...others }) {
    Vue.set(state.rooms, id, { ...others });
  },
  [SET_TEXTUAL_MESSAGE](state, { id, ...others }) {
    Vue.set(state.textuals, id, { ...others });
  },
  [DEL_TEXTUAL_MESSAGE](state, { id }) {
    Vue.delete(state.textuals, id);
  },
  [SET_TOPIC](state, { id, ...others }) {
    Vue.set(state.topics, id, { ...others });
  },
  [SET_COMMENT](state, { id, ...others }) {
    Vue.set(state.comments, id, { ...others });
  },
  [SET_SUBCOMMENT](state, { id, ...others }) {
    Vue.set(state.subcomments, id, { ...others });
  },
  [SET_REACTION](state, { id, ...others }) {
    Vue.set(state.reactions, id, { ...others });
  },
  [DEL_TOPIC](state, { id }) {
    Vue.delete(state.topics, id);
  },
  [DEL_COMMENT](state, { id }) {
    Vue.delete(state.comments, id);
  },
  [DEL_SUBCOMMENT](state, { id }) {
    Vue.delete(state.subcomments, id);
  },
  [DEL_REACTION](state, { id }) {
    Vue.delete(state.reactions, id);
  },

  [SET_MEMBER](state, { uid, ...others }) {
    Vue.set(state.members, uid, { ...others });
  },
  [SET_MEMBERS_ONLINE](state, { uid, action, ...others }) {
    if (action === "connected") Vue.set(state.membersOnline, uid, others);
    else Vue.delete(state.members, uid);
  },
  [SET_TOTAL_MEMBERS](state, { ...others }) {
    Vue.set(state, "membersTotal", { ...others });
  },

  [SET_KEY](state, { key, value }) {
    Vue.set(state, key, value);
  },

  [SHOW_MODAL_ADD](state, value) {
    state.isShowingModalAdd = value;
  },

  [SHOW_COMMENT_EDIT](state, value) {
    state.isOpenCommentEdit = value;
  },
  [HIDE_COMMENT_EDIT](state, value) {
    state.isOpenCommentEdit = value;
  },
  [SET_QUERY_RESULT](state, value) {
    state.queryResult = value;
  },
  [SET_DELETE_RESULT](state, value) {
    state.deleteResult = value;
  },
};

const actions = {
  // CHANNELS
  async [FETCH_CHANNELS]({ commit, rootGetters }, payload) {
    const { role, ...user } = rootGetters["auth/currentUser"];
    const result = ref(database, `channels`);

    await onValue(result, (snapshot) => {
      const data = snapshot.val();

      if (data)
        Object.entries(data).forEach(([id, data]) => {
          const { hasAccess } = data;
          if (hasAccess.includes("all")) commit(SET_CHANNEL, { id, ...data });
          else if (hasAccess.includes("admin") && role === "admin") commit(SET_CHANNEL, { id, ...data });
        });
    });
  },
  [CREATE_CHANNEL]({ commit, rootGetters }, { config, hasAccess }) {
    const { uid } = rootGetters["auth/currentUser"];

    const createdAt = serverTimestamp();
    config.createdAt = createdAt;
    const members = {
      [uid]: {
        role: "owner",
        addedOn: createdAt,
      },
    };

    // Create channel
    const id = push(child(ref(database), "channels")).key;
    set(ref(database, `channels/${id}`), { config, members, hasAccess });
  },
  async [GET_CHANNEL]({ state }, { channelId }) {
    const { channels } = state;
    if (!channels[channelId] || !Object.keys(channels[channelId]).length) {
      const channelRef = query(ref(database, "channels"));
      const snpChannel = await get(channelRef);
      const datas = snpChannel.val();
      return datas[channelId];
    }
    return channels[channelId];
  },
  [GOTO_CHANNEL]({ commit }, { id }) {
    commit(SET_KEY, { key: "currentChannelId", value: id });
  },

  // TOPICS
  async [FETCH_TOPICS]({ state, commit }, { channelId, page = 1, ...payload }) {
    if (
      !state.queryResult ||
      state.queryResult.type !== "topic" ||
      page === 1 ||
      (page - 1) * TOPICS_IN_CHANNEL < state.queryResult?.total
    ) {
      const topicsRef = query(
        ref(database, "topics"),
        orderByChild("channelId"),
        equalTo(channelId),
        limitToLast(TOPICS_IN_CHANNEL * page)
      );

      onChildAdded(topicsRef, (data) => {
        const result = data.val();
        commit(SET_TOPIC, { id: data.key, ...result });
      });

      onChildChanged(topicsRef, (data) => {
        const result = data.val();
        commit(SET_TOPIC, { id: data.key, ...result });
      });

      onChildRemoved(topicsRef, (data) => {
        commit(SET_DELETE_RESULT, { type: "topics", id: data.key, result: "success" });
        setTimeout(() => {
          commit(DEL_TOPIC, { id: data.key });
          commit(SET_DELETE_RESULT, null);
        }, 2000);
      });

      const resultData = await get(query(ref(database, "topics"), orderByChild("channelId"), equalTo(channelId)));
      commit(SET_QUERY_RESULT, { type: "topic", total: resultData.size, page, limit: TOPICS_IN_CHANNEL });
    }
  },
  async [GET_TOPIC_BY_ID]({ state, commit }, { topicId }) {
    let { topics } = state;
    if (topics[topicId]) return topics[topicId];
    const topicsRef = query(ref(database, `topics/${topicId}`));
    const snpTopic = await get(topicsRef);
    const topic = snpTopic.val();
    topics = { ...topics, [snpTopic.key]: topic };
    state.topics = topics;
    return topic;
  },
  async [GET_TOPIC]({ state, commit }, { topicId, page = 1, ...payload }) {
    let { topics } = state;

    const commentsRef = query(
      ref(database, "comments"),
      orderByChild("topicId"),
      equalTo(topicId),
      limitToLast(COMMENTS_IN_TOPIC * page)
    );

    onChildAdded(commentsRef, (data) => {
      const result = data.val();
      commit(SET_COMMENT, { topicId, id: data.key, ...result });
    });

    onChildChanged(commentsRef, (data) => {
      const result = data.val();
      commit(SET_COMMENT, { topicId, id: data.key, ...result });
    });

    onChildRemoved(commentsRef, (data) => {
      commit(SET_DELETE_RESULT, { type: "comments", id: data.key, result: "success" });
      setTimeout(() => {
        commit(DEL_COMMENT, { id: data.key });
        commit(SET_DELETE_RESULT, null);
      }, 2000);
    });

    const reactionsRef = query(ref(database, "reactions"), orderByChild("topicId"), equalTo(topicId));
    onChildAdded(reactionsRef, (data) => {
      const result = data.val();
      commit(SET_REACTION, { topicId, id: data.key, ...result });
    });

    onChildRemoved(reactionsRef, (data) => {
      commit(DEL_REACTION, { id: data.key });
    });

    const resultData = await get(query(ref(database, "comments"), orderByChild("topicId"), equalTo(topicId)));
    commit(SET_QUERY_RESULT, { type: "comment", total: resultData.size, page, limit: COMMENTS_IN_TOPIC });

    if (topics[topicId]) return topics[topicId];
    const topicsRef = query(ref(database, "topics/" + topicId));
    onChildAdded(topicsRef, (data) => {
      topics = { ...topics, [data.key]: data.val() };
      state.topics = { [topicId]: topics };
    });
    onChildChanged(topicsRef, (data) => {
      topics = { ...topics, [data.key]: data.val() };
      state.topics = { [topicId]: topics };
    });
    onChildRemoved(topicsRef, (data) => {
      commit(SET_DELETE_RESULT, { type: "topics", id: topicId, result: "success" });
      setTimeout(() => {
        commit(DEL_TOPIC, { id: topicId });
        commit(SET_DELETE_RESULT, null);
      }, 2000);
    });
    return {};
  },
  async [CREATE_TOPIC]({ rootGetters, commit }, { channelId, title, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];

    const createdAt = serverTimestamp();

    // // Create topic
    const id = push(child(ref(database), "topics")).key;
    set(ref(database, `topics/${id}`), { channelId, uid, title, createdAt, ...payload });

    // Add topics to owner list and channel
    set(ref(database, `members/${uid}/topics/${id}`), { channelId, role: "owner", title, createdAt });
    set(ref(database, `channels/${channelId}/topics/${id}`), { uid, title, createdAt });

    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);

    // Add notification
    const eventData = { event: "topic", action: "create", id, channelId, createdAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);
    return { id };
  },

  async [UPDATE_TOPIC]({ rootGetters, commit }, { channelId, topicId, title, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];

    const updatedAt = serverTimestamp();

    // Update topic
    update(ref(database, `topics/${topicId}`), { channelId, uid, title, updatedAt, ...payload });

    // Update topics to owner list and channel
    update(ref(database, `members/${uid}/topics/${topicId}`), { title, updatedAt });
    update(ref(database, `channels/${channelId}/topics/${topicId}`), { title, updatedAt });

    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);

    // Add notification
    const eventData = { event: "topic", action: "update", id: topicId, channelId, createdAt: updatedAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);
    return { id: topicId };
  },

  async [REMOVE_TOPIC]({ state, commit }, { channelId, topicId, uid, ...payload }) {
    await remove(ref(database, `topics/${topicId}`));
    await get(child(ref(database), `comments`)).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        snapshot.forEach((doc) => {
          if (data[doc.key].topicId === topicId) {
            remove(ref(database, `comments/${doc.key}`));
            remove(ref(database, `members/${uid}/comments/${doc.key}`));
          }
        });
      }
    });
    await get(child(ref(database), `subcomments`)).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        snapshot.forEach((doc) => {
          if (data[doc.key].topicId === topicId) {
            remove(ref(database, `subcomments/${doc.key}`));
            remove(ref(database, `members/${uid}/subcomments/${doc.key}`));
          }
        });
      }
    });
    await remove(ref(database, `members/${uid}/topics/${topicId}`));
    await remove(ref(database, `channels/${channelId}/topics/${topicId}`));

    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);
    const createdAt = serverTimestamp();
    // Add notification
    const eventData = { event: "topic", action: "remove", id: topicId, channelId, createdAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);
    return;
  },
  async [CREATE_COMMENT]({ rootGetters }, { channelId, topicId, content, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];

    const createdAt = serverTimestamp();

    const id = push(child(ref(database), "comments")).key;
    set(ref(database, `comments/${id}`), { channelId, topicId, uid, createdAt, content, ...payload });
    set(ref(database, `members/${uid}/comments/${id}`), { channelId, topicId, role: "owner", content, createdAt });

    runTransaction(ref(database, `topics/${topicId}/countComments`), (count) => {
      if (count) {
        count++;
      } else {
        count = 1;
      }

      return count;
    });

    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);
    // Add notification
    const eventData = { event: "comment", action: "create", id, channelId, topicId, createdAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    // console.log("memberIds: ", memberIds);
    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);

    return { id };
  },
  async [UPDATE_COMMENT]({ rootGetters }, { channelId, topicId, commentId, content, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];

    const updatedAt = serverTimestamp();

    update(ref(database, `comments/${commentId}`), { updatedAt, content, ...payload });
    update(ref(database, `members/${uid}/comments/${commentId}`), { content, updatedAt });

    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);
    const createdAt = serverTimestamp();
    // Add notification
    const eventData = { event: "comment", action: "update", id: commentId, channelId, topicId, createdAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    // console.log("memberIds: ", memberIds);
    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);

    return { id: commentId };
  },
  async [REMOVE_COMMENT]({ state, commit }, { channelId, topicId, commentId, uid, ...payload }) {
    remove(ref(database, `comments/${commentId}`));

    get(child(ref(database), `subcomments`)).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        snapshot.forEach((doc) => {
          if (data[doc.key].commentId === commentId) {
            remove(ref(database, `subcomments/${doc.key}`));
            remove(ref(database, `members/${uid}/subcomments/${doc.key}`));
          }
        });
      }
    });

    remove(ref(database, `members/${uid}/comments/${commentId}`));
    runTransaction(ref(database, `topics/${topicId}/countComments`), (count) => {
      if (count) {
        count--;
      } else {
        count = 0;
      }
      return count;
    });

    const createdAt = serverTimestamp();
    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);
    // Add notification
    const eventData = { event: "comment", action: "remove", id: commentId, channelId, topicId, createdAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    // console.log("memberIds: ", memberIds);
    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);
  },
  async [GET_REACTION_BY_MSGID]({ state }, { commentId, ...payload }) {
    let { reactions } = state;
    if (reactions[commentId]) return reactions[commentId];
    const emojiRef = query(ref(database, `reactions`));
    const snpEmoji = await get(emojiRef);
    const msgEmojis = snpEmoji.val();
    if (msgEmojis) {
      reactions = msgEmojis;
      state.reactions = reactions;
    }
    return msgEmojis;
  },
  [CREATE_REACTION](
    { rootGetters },
    { channelId = null, topicId = null, messageId = null, commentId = null, subCommentId = null, emoji, ...payload }
  ) {
    const { uid } = rootGetters["auth/currentUser"];
    const createdAt = serverTimestamp();
    const id = push(child(ref(database), "reactions")).key;
    set(ref(database, `reactions/${id}`), {
      channelId,
      topicId,
      commentId,
      messageId,
      subCommentId,
      uid,
      createdAt,
      emoji,
      ...payload,
    });
    set(ref(database, `members/${uid}/reactions/${id}`), {
      channelId,
      messageId,
      topicId,
      commentId,
      subCommentId,
      role: "owner",
      emoji,
      createdAt,
    });
    // Reactions in comments are not counted in topics reactions
    if (!subCommentId) {
      if (!commentId) {
        runTransaction(ref(database, `topics/${topicId}/countReactions`), (count) => {
          if (count) {
            count++;
          } else {
            count = 1;
          }

          return count;
        });
      } else {
        runTransaction(ref(database, `comments/${commentId}/countReactions`), (count) => {
          if (count) {
            count++;
          } else {
            count = 1;
          }

          return count;
        });
      }
    }

    return { id };
  },
  [REMOVE_REACTION]({ rootGetters }, { topicId = null, commentId = null, reactionId = null }) {
    const { uid } = rootGetters["auth/currentUser"];

    remove(ref(database, `reactions/${reactionId}`));
    remove(ref(database, `members/${uid}/reactions/${reactionId}`));

    // Reactions in comments are not counted in topics reactions
    if (!commentId)
      runTransaction(ref(database, `topics/${topicId}/countReactions`), (count) => {
        if (count) {
          count--;
        }

        return count;
      });
  },
  async [GET_COMMENT]({ state, commit }, { commentId, page = 1, ...payload }) {
    const { comments } = state;
    const subCommentsRef = query(
      ref(database, "subcomments"),
      orderByChild("commentId"),
      equalTo(commentId),
      limitToLast(SUBCOMMENTS_IN_COMMENTS * page)
    );

    onChildAdded(subCommentsRef, (data) => {
      const result = data.val();
      commit(SET_SUBCOMMENT, { commentId, id: data.key, ...result });
    });
    onChildChanged(subCommentsRef, (data) => {
      const result = data.val();
      commit(SET_SUBCOMMENT, { commentId, id: data.key, ...result });
    });
    onChildRemoved(subCommentsRef, (data) => {
      commit(SET_DELETE_RESULT, { type: "subcomments", id: data.key, result: "success" });
      setTimeout(() => {
        commit(DEL_SUBCOMMENT, { id: data.key });
        commit(SET_DELETE_RESULT, null);
      }, 2000);
    });

    const reactionsRef = query(ref(database, "reactions"), orderByChild("commentId"), equalTo(commentId));
    onChildAdded(reactionsRef, (data) => {
      const result = data.val();
      commit(SET_REACTION, { commentId, id: data.key, ...result });
    });

    onChildRemoved(reactionsRef, (data) => {
      commit(DEL_REACTION, { id: data.key });
    });

    const resultData = await get(query(ref(database, "subcomments"), orderByChild("commentId"), equalTo(commentId)));
    commit(SET_QUERY_RESULT, { type: "subcomment", total: resultData.size, page, limit: SUBCOMMENTS_IN_COMMENTS, id: commentId });

    if (comments[commentId]) return comments[commentId];

    return {};
  },
  async [GET_COMMENT_BY_ID]({ state }, { commentId, ...payload }) {
    let { comments } = state;
    if (comments[commentId]) return comments[commentId];
    const commentsRef = query(ref(database, `comments/${commentId}`));
    const snpComment = await get(commentsRef);
    const comment = snpComment.val();
    if (comment) {
      comments = { ...comments, [snpComment.key]: comment };
      state.comments = comments;
    }
    return comment;
  },
  async [GET_SUBCOMMENT_BY_ID]({ state }, { subCommentId, ...payload }) {
    let { subcomments } = state;
    if (subcomments[subCommentId]) return subcomments[subCommentId];
    const subcommentsRef = query(ref(database, `subcomments/${subCommentId}`));
    const snpSubComment = await get(subcommentsRef);
    const subcomment = snpSubComment.val();
    if (subcomment) {
      subcomments = { ...subcomments, [snpSubComment.key]: subcomment };
      state.subcomments = subcomments;
    }
    return subcomment;
  },

  async [CREATE_SUBCOMMENT]({ rootGetters }, { channelId, topicId, commentId, content, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];

    const createdAt = serverTimestamp();

    const id = push(child(ref(database), "subcomments")).key;
    set(ref(database, `subcomments/${id}`), { channelId, topicId, commentId, uid, createdAt, content, ...payload });
    set(ref(database, `members/${uid}/subcomments/${id}`), { channelId, topicId, commentId, role: "owner", content, createdAt });

    runTransaction(ref(database, `comments/${commentId}/countComments`), (count) => {
      if (count) {
        count++;
      } else {
        count = 1;
      }

      return count;
    });

    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);
    // Add notification
    const eventData = { event: "subcomment", action: "create", id, channelId, topicId, commentId, createdAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    // console.log("memberIds: ", memberIds);
    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);

    return { id };
  },
  async [UPDATE_SUBCOMMENT]({ rootGetters }, { channelId, topicId, commentId, subCommentId, content, ...payload }) {
    const { uid } = rootGetters["auth/currentUser"];

    const updatedAt = serverTimestamp();

    update(ref(database, `subcomments/${subCommentId}`), { updatedAt, content, ...payload });
    update(ref(database, `members/${uid}/subcomments/${subCommentId}`), { content, updatedAt });

    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);
    // Add notification
    const eventData = {
      event: "subcomment",
      action: "update",
      id: subCommentId,
      channelId,
      topicId,
      commentId,
      createdAt: updatedAt,
      uid,
    };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    // console.log("memberIds: ", memberIds);
    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);

    return { id: subCommentId };
  },
  [UPDATE_USER_READING]({ state, commit }, { topicId, flag, ...payload }) {
    runTransaction(ref(database, `topics/${topicId}/countUsers`), (count) => {
      if (count) {
        flag ? count++ : count--;
      } else {
        flag ? (count = 1) : (count = 0);
      }

      return count;
    });
  },
  [UPDATE_USER_REPLAY]({ state, commit }, { status, topicId, commentId = null, ...payload }) {
    if (commentId) {
      runTransaction(ref(database, `comments/${commentId}/replying`), (count) => {
        if (count) {
          status ? count++ : count--;
        } else {
          status ? (count = 1) : (count = 0);
        }
        return count;
      });
    } else if (topicId) {
      runTransaction(ref(database, `topics/${topicId}/replying`), (count) => {
        if (count) {
          status ? count++ : count--;
        } else {
          status ? (count = 1) : (count = 0);
        }
        return count;
      });
    }
  },
  [UPDATE_OPEN_COMMENT_EDIT]({ state, commit }, { at, id, ...payload }) {
    if (!state.isOpenCommentEdit) {
      commit(SHOW_COMMENT_EDIT, { at, id });
    } else {
      if (state.isOpenCommentEdit.at === at && state.isOpenCommentEdit.id === id) {
        commit(HIDE_COMMENT_EDIT, null);
      } else {
        commit(SHOW_COMMENT_EDIT, { at, id });
      }
    }
  },
  [UPDATE_QUERY_RESULT]({ state }, value) {
    state.queryResult = value;
  },
  async [REMOVE_SUBCOMMENT]({ state, commit }, { channelId, topicId, commentId, subCommentId, uid, ...payload }) {
    remove(ref(database, `subcomments/${subCommentId}`));
    remove(ref(database, `members/${uid}/subcomments/${subCommentId}`));
    runTransaction(ref(database, `comments/${commentId}/countComments`), (count) => {
      if (count) {
        count--;
      } else {
        count = 0;
      }
      return count;
    });

    const createdAt = serverTimestamp();
    // get members to recieve notification
    const memberIds = await getAllMembersInChannel(channelId);
    // Add notification
    const eventData = { event: "subcomment", action: "remove", id: subCommentId, channelId, topicId, commentId, createdAt, uid };
    const notificationId = push(child(ref(database), "notifications")).key;
    set(ref(database, `notifications/${notificationId}`), eventData);

    // console.log("memberIds: ", memberIds);
    const promises = memberIds.map((memberId) => memberId !== uid && addNotificationToUser(memberId, notificationId, eventData));
    await Promise.all(promises);
  },

  // MEMBERS
  async [GET_MEMBER]({ state, commit }, { uid }) {
    if (!uid) return {};

    if (!state.members[uid] || !state.members[uid].length)
      await onValue(
        ref(database, `/members/${uid}/public`),
        (snapshot) => {
          const data = snapshot.val();
          commit(SET_MEMBER, { uid, ...data });
        },
        {
          onlyOnce: true,
        }
      );

    return state.members[uid];
  },
  async [FETCH_MEMBERS_TOTAL]({ commit }) {
    await onValue(
      ref(database, `members`),
      (snapshot) => {
        const data = snapshot.val();
        commit(SET_KEY, { key: "membersTotal", value: data });
      },
      {
        onlyOnce: true,
      }
    );
  },
  async [GET_MEMBER_BY_ID]({ state, commit }, { uid }) {
    if (!uid) return {};

    if (!state.members[uid] || !state.members[uid].length) {
      const snpMember = await get(ref(database, `/members/${uid}/public`));
      const data = snpMember.val();
      commit(SET_MEMBER, { uid, ...data });
      return data;
    }
    return state.members[uid];
  },
  async [FETCH_MEMBERS_BY_CHANNEL_ID]({ state, commit }, { channelId }) {
    if (!channelId) return [];

    const channelMemberRef = query(ref(database, `/channels/members`), orderByChild("channelId"));

    onChildAdded(channelMemberRef, (data) => {
      const result = data.val();
      commit(SET_MEMBER, { id: data.key, ...result });
    });
  },
  [FETCH_MEMBERS_ONLINE]({ commit }) {
    onChildAdded(ref(database, "membersOnline"), (data) => {
      commit(SET_MEMBERS_ONLINE, { uid: data.key, ...data.val(), action: "connected" });
    });

    onChildRemoved(ref(database, "membersOnline"), (data) => {
      commit(SET_MEMBERS_ONLINE, { uid: data.key, action: "disconnected" });
    });
  },

  [OPEN_MODAL_ADD]({ commit }) {
    commit(SHOW_MODAL_ADD, true);
  },
  [CLOSE_MODAL_ADD]({ commit }) {
    commit(SHOW_MODAL_ADD, false);
  },

  [SHOW_DELETE_TOAST]({ commit }, payload) {
    commit(SET_DELETE_RESULT, payload);
  },

  // Rooms
  [CREATE_ROOM]({ commit, rootGetters }, { config, protection }) {
    const { uid } = rootGetters["auth/currentUser"];
    const createdAt = serverTimestamp();
    config.createdAt = createdAt;
    const members = {
      [uid]: {
        role: "owner",
        addedOn: createdAt,
      },
    };
    const id = push(child(ref(database), "rooms")).key;
    set(ref(database, `rooms/${id}`), { config, members, protection });

    // Add room to owner list
    set(ref(database, `members/${uid}/rooms/${id}`), { config, role: "owner", addedOn: createdAt });
  },

  async [FETCH_ROOMS]({ commit }, payload) {
    const snpRooms = await get(query(ref(database, "rooms")));
    if (!snpRooms.exists) {
      return [];
    }
    snpRooms.forEach((room) => {
      commit(SET_ROOM, { id: room.key, ...room.val() });
    });
    onChildAdded(ref(database, "rooms"), (snapshot) => {
      const data = snapshot.val();
      commit(SET_ROOM, { id: snapshot.key, ...data });
    });

    onChildChanged(ref(database, "rooms"), (snapshot) => {
      const data = snapshot.val();
      commit(SET_ROOM, { id: snapshot.key, ...data });
    });
  },
  async [FETCH_MESSAGES_IN_ROOM]({ commit, rootGetters }, payload) {
    const { roomId, page = 1 } = payload;
    const { uid } = rootGetters["auth/currentUser"];
    const messageQuery = query(
      ref(database, "textuals"),
      orderByChild("roomId"),
      equalTo(roomId),
      limitToLast(MESSAGES_IN_PAGE * page)
    );

    onChildAdded(messageQuery, (snapshot) => {
      const data = snapshot.val();
      commit(SET_TEXTUAL_MESSAGE, { id: snapshot.key, ...data });
    });
    onChildChanged(messageQuery, (snapshot) => {
      const data = snapshot.val();
      commit(SET_TEXTUAL_MESSAGE, { id: snapshot.key, ...data });
    });
    onChildRemoved(messageQuery, (data) => {
      commit(DEL_TEXTUAL_MESSAGE, { id: data.key });
    });

    const resultData = await get(query(ref(database, "textuals"), orderByChild("roomId"), equalTo(roomId)));
    const resultCount = resultData.size;

    const readAt = serverTimestamp();
    const data = resultData.val();

    if (!data) return;

    const resultArr = Object.entries(data);
    update(ref(database, `rooms/${roomId}/members/${uid}`), { readAt: readAt, lastId: resultArr[resultCount - 1][0], unread: 0 });

    commit(SET_QUERY_RESULT, { type: "message", total: resultCount, page, limit: MESSAGES_IN_PAGE });
  },
  [UPDATE_UNREAD_COUNT_IN_ROOM]({ commit, rootGetters }, payload) {
    const { roomId, unreadCount, userId } = payload;
    update(ref(database, `rooms/${roomId}/members/${userId}`), { unread: unreadCount });
  },
  async [SEND_MESSAGE_IN_ROOM]({ commit, rootGetters }, payload) {
    const { uid } = rootGetters["auth/currentUser"];
    const { total, page } = rootGetters["community/queryResult"];
    const { roomId, messageObj } = payload;

    const membersData = await get(child(ref(database), `rooms/${roomId}/members`));
    if (membersData.exists) {
      Object.entries(membersData.val()).forEach((member) => {
        if (member[0] !== uid) {
          runTransaction(ref(database, `rooms/${roomId}/members/${member[0]}/unread`), (count) => {
            if (count) {
              isNaN(count) ? (count = 1) : count++;
            } else {
              count = 1;
            }
            return count;
          });
        }
      });
    }

    const messageRef = ref(database, "textuals");

    const createdAt = serverTimestamp();
    messageObj.createdAt = createdAt;
    messageObj.sender = uid;
    // messageObj.uid = messageId;
    messageObj.roomId = roomId;

    const messageId = push(messageRef).key;
    messageObj.uid = messageId;

    set(ref(database, `textuals/${messageId}`), messageObj);
    update(ref(database, `rooms/${roomId}/members/${uid}`), { readAt: createdAt, lastId: messageId });
    commit(SET_QUERY_RESULT, { type: "message", total: total + 1, page, limit: MESSAGES_IN_PAGE });
  },
  async [UPDATE_MESSAGE_IN_ROOM]({ commit, rootGetters }, payload) {
    const { roomId, messageObj } = payload;
    const { uid } = rootGetters["auth/currentUser"];
    const createdAt = serverTimestamp();
    messageObj.updatedAt = createdAt;
    messageObj.sender = uid;
    const messageId = messageObj["uid"];

    update(ref(database, `textuals/${messageId}`), messageObj);
  },
  async [DELETE_MESSAGE_IN_ROOM]({ commit, rootGetters }, payload) {
    const { messageId } = payload;
    const { total, page } = rootGetters["community/queryResult"];
    remove(ref(database, `textuals/${messageId}`));
    commit(SET_QUERY_RESULT, { type: "message", total: total - 1, page, limit: MESSAGES_IN_PAGE });
  },

  async [LOGIN_ROOM]({ rootGetters }, payload) {
    try {
      const { uid } = rootGetters["auth/currentUser"];
      const { data } = await triggerRoomLogin({ uid, ...payload });

      return data;
    } catch (error) {
      console.log("ERR", error);
      return { isSuccess: false };
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
